import { Component, Input } from '@angular/core'
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { TranslateService } from '@ngx-translate/core'
import { APIService } from 'api/module'
import { NotifyService } from 'common/util/services/notify.service'
import { replaceArrayItems } from 'common/helpers'
import { MessageBoxService } from 'common/util/module'
import { MediaRootDetail, UnfilteredTag, UnfilteredTagUpdate } from 'elements-sdk'
import { PickerItem, PickerService } from 'common/util.pickers/module'

const BATCH_SIZE = 250
const PAGE_SIZE = 25

@Component({
  templateUrl: './mediaTagsManagerModal.component.pug',
})
export class MediaTagsManagerModalComponent {
  @Input() root: MediaRootDetail
  @Input() shared = false
  loading = false
  tags?: UnfilteredTag[]
  tagColors: PickerItem<string>[] = [
    {
      object: '#5BA4D9',
      iconClass: 'fas fa-circle text-info',
      name: this.translate.instant('Blue'),
    },
    {
      object: '#E09C3D',
      iconClass: 'fas fa-circle text-warning',
      name: this.translate.instant('Yellow'),
    },
    {
      object: '#6BBD32',
      iconClass: 'fas fa-circle text-success',
      name: this.translate.instant('Green'),
    },
    {
      object: '#BF1501',
      iconClass: 'fas fa-circle text-danger',
      name: this.translate.instant('Red'),
    },
    {
      object: '#777777',
      iconClass: 'fas fa-circle text-secondary',
      name: this.translate.instant('Gray'),
    },
  ]
  pagination = {
    page: 1,
    pageSize: PAGE_SIZE,
  }

  constructor (
    private modalInstance: NgbActiveModal,
    private api: APIService,
    private ngbModal: NgbModal,
    private messageBox: MessageBoxService,
    private translate: TranslateService,
    private notify: NotifyService,
    private picker: PickerService,
  ) { }

  ngOnInit () {
    this.loadTags(0)
  }

  async loadTags (offset) {
    this.loading = true
    const response = await this.api.mediaLibrary.getAllMediaTagsRaw({
      roots: this.shared ? undefined : this.root.id.toString(),
      shared: this.shared.toString(),
      offset,
      limit: BATCH_SIZE,
    })
    const tags = await response.value()
    const pagination = this.api.extractPagination(response)
    this.tags = this.tags ? this.tags.concat(tags) : tags
    if (this.tags.length < (pagination?.total ?? 0)) {
      this.loadTags(offset + BATCH_SIZE)
    } else {
      this.loading = false
    }
  }

  async createTag (name) {
    if (!name) {
      return
    }
    if (this.tags?.some(x => x.name === name)) {
      this.notify.error(this.translate.instant('Tag already exists'))
      return
    }

    const tags = await this.api.mediaLibrary.getAllMediaTags({ name })
    if (tags.length === 0) {
      const tagData: UnfilteredTagUpdate = {
        name,
        shared: this.shared,
        roots: [],
      }
      tagData.roots.push(this.root.id)

      try {
        const tag = await this.api.mediaLibrary.createMediaTag({ unfilteredTagUpdate: tagData })
        this.tags = [...this.tags!, tag]
        this.notify.info(this.translate.instant('Tag added'))
      } catch (e) {
        this.notify.error(this.translate.instant('Could not create tag'))
      }
    } else {
      const tagData = {
        ...tags[0],
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        shared: tags[0].shared || this.shared,
      }
      tagData.roots.push(this.root.id)

      try {
        const tag = await this.api.mediaLibrary.patchMediaTag({
          id: tagData.id,
          unfilteredTagPartialUpdate: tagData,
        })
        this.tags = [...this.tags!, tag]
        this.notify.info(this.translate.instant('Tag added'))
      } catch (e) {
        this.notify.error(this.translate.instant('Could not attach tag'))
      }
    }
  }

  async deleteTag (tag) {
    try {
      await this.messageBox.deletePrompt(this.translate.instant('tag', 'delete [tag]?'), tag.name)
    } catch {
      return
    }
    if (tag.roots?.length === 1 && tag.roots[0] === this.root.id) {
      this.api.mediaLibrary.deleteMediaTag(tag)
    } else {
      if (this.shared) {
        tag.shared = false
      }
      tag.roots = tag.roots.filter(x => x !== this.root.id)
      this.api.mediaLibrary.patchMediaTag({
        id: tag.id,
        unfilteredTagPartialUpdate: tag,
      })
    }
    this.tags = this.tags!.filter(x => x !== tag)
  }

  async editTag (tag) {
    const tagPrompt = await this.messageBox.prompt({ prompt: this.translate.instant('New tag name'), value: tag.name }).catch(() => null)
    if (!tagPrompt) {
      return
    }
    try {
      const updatedTag = await this.api.mediaLibrary.patchMediaTag({
        id: tag.id,
        unfilteredTagPartialUpdate: { name: tagPrompt.value },
      })
      this.notify.success(this.translate.instant('Tag renamed'))
      this.tags = replaceArrayItems(this.tags!, x => x === tag, () => updatedTag)
    } catch (error) {
      this.notify.errorFromResponse(this.translate.instant('Could not rename tag'), error)
    }
  }

  close () {
    this.modalInstance.dismiss()
  }

  showShared () {
    this.modalInstance.dismiss()
    const m = this.ngbModal.open(MediaTagsManagerModalComponent)
    m.componentInstance.shared = true
    m.componentInstance.root = this.root
    m.result.catch(() => null)
  }

  async selectColor (tag: UnfilteredTag) {
    const color = await this.picker.pickObject({
      items: this.tagColors,
    }).catch(() => null)
    if (!color) {
      return
    }
    const updatedTag = await this.api.mediaLibrary.patchMediaTag({
      id: tag.id,
      unfilteredTagPartialUpdate: { color },
    })
    this.tags = replaceArrayItems(this.tags!, x => x === tag, () => updatedTag)
  }
}
